import React, { useState, useRef } from 'react';
import { Upload, X, Image, Camera, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * PhotoUpload component for mini-move quotes
 * Allows users to upload photos of items to be moved
 * Photos are converted to base64 for storage
 */
const PhotoUpload = ({ onPhotosChange, maxPhotos = 5 }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setUploading(true);

    try {
      const newPhotos = await Promise.all(
        files.map(async (file) => {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            throw new Error(`${file.name} is not an image`);
          }

          // Validate file size (max 5MB)
          if (file.size > 5 * 1024 * 1024) {
            throw new Error(`${file.name} is too large (max 5MB)`);
          }

          // Convert to base64
          const base64 = await fileToBase64(file);
          
          return {
            id: Date.now() + Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            preview: base64,
            base64: base64
          };
        })
      );

      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onPhotosChange(updatedPhotos.map(p => p.base64));
      toast.success(`${files.length} photo(s) added`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const removePhoto = (id) => {
    const updatedPhotos = photos.filter(p => p.id !== id);
    setPhotos(updatedPhotos);
    onPhotosChange(updatedPhotos.map(p => p.base64));
    toast.success('Photo removed');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div 
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          uploading ? 'border-[#039fff] bg-[#039fff]/5' : 'border-gray-300 hover:border-[#039fff]'
        }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          multiple
          className="hidden"
          disabled={uploading || photos.length >= maxPhotos}
        />
        
        <div className="flex flex-col items-center">
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#039fff] mb-3"></div>
              <p className="text-sm text-gray-600">Processing photos...</p>
            </>
          ) : (
            <>
              <div className="bg-[#039fff]/10 rounded-full p-3 mb-3">
                <Camera className="h-8 w-8 text-[#039fff]" />
              </div>
              <p className="text-sm font-medium text-[#001A47] mb-1">
                Add photos of your items
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Helps us provide an accurate quote
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={photos.length >= maxPhotos}
              >
                <Upload className="h-4 w-4 mr-2" />
                {photos.length >= maxPhotos ? 'Maximum reached' : 'Choose Photos'}
              </Button>
              <p className="text-xs text-gray-400 mt-2">
                {photos.length}/{maxPhotos} photos • Max 5MB each
              </p>
            </>
          )}
        </div>
      </div>

      {/* Photo Preview Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((photo) => (
            <div 
              key={photo.id} 
              className="relative group rounded-lg overflow-hidden border bg-gray-50"
            >
              <img
                src={photo.preview}
                alt={photo.name}
                className="w-full h-24 object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                <p className="text-white text-xs truncate">{formatFileSize(photo.size)}</p>
              </div>
              <div className="absolute top-1 right-1">
                <CheckCircle className="h-4 w-4 text-green-500 bg-white rounded-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-xs font-medium text-[#001A47] mb-2">Photo tips:</p>
        <ul className="text-xs text-gray-600 space-y-1">
          <li className="flex items-start">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
            Take photos in good lighting
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
            Include all furniture and large items
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-3 w-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
            Show the number of boxes if applicable
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PhotoUpload;
